import { toast } from '@/components/UI/Toast';
import { $bbbeeWebApi } from '@/http/OpenAPI/clients/react-query/bbbee';
import { $coiApi } from '@/http/OpenAPI/clients/react-query/coi';
import { $onboardingWorkflowApi } from '@/http/OpenAPI/clients/react-query/onboarding-workflows';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * QA Playground demo data (Feature #8595, US-5): "Load Demo Data"/"Reset Demo Data" fan out to
 * three independent services' own seed/reset endpoints. Company's own seed-companies endpoint
 * is deliberately NOT called here - it is BackendOnly (service-to-service only) and is already
 * called server-to-server by each of these three handlers before they attach their own
 * fabricated records, so calling it again from the browser would be both unauthorized and
 * redundant.
 *
 * Promise.allSettled (not Promise.all): one service's seed/reset failing must not hide that the
 * other two succeeded - same reasoning as useCompanySupportingFileIds's upload fan-out. The three
 * calls have no ordering dependency on each other - each independently calls
 * ICompanyApiClient.SeedDemoCompanyPairAsync before attaching its own records, so on the very
 * first "Load Demo Data" click, two or three of them can race to create the SAME demo company
 * row at once. That race is made safe on the Company side, not here: it is an optimistic
 * insert-then-detect-conflict (PlaygroundDemoCompanyBusinessLogic.GetOrCreateDemoCompanyAsync
 * catches the loser's unique-key violation and re-reads the winner's row), so every concurrent
 * caller ends up with the same company id regardless of arrival order - firing all three in
 * parallel here is what actually exercises that path.
 */

export type PlaygroundDemoDataServiceName = 'Onboarding' | 'BBBEE' | 'COI';

export interface PlaygroundDemoDataActionResult {
  succeeded: PlaygroundDemoDataServiceName[];
  failed: PlaygroundDemoDataServiceName[];
}

async function runAcrossServices(
  calls: ReadonlyArray<{
    service: PlaygroundDemoDataServiceName;
    run: () => Promise<unknown>;
  }>
): Promise<PlaygroundDemoDataActionResult> {
  const settled = await Promise.allSettled(calls.map((c) => c.run()));

  const succeeded: PlaygroundDemoDataServiceName[] = [];
  const failed: PlaygroundDemoDataServiceName[] = [];

  settled.forEach((result, index) => {
    const { service } = calls[index];
    if (result.status === 'fulfilled') {
      succeeded.push(service);
    } else {
      failed.push(service);
      console.error(
        `QA Playground demo data action failed for ${service}:`,
        result.reason
      );
    }
  });

  return { succeeded, failed };
}

const OUTCOME_COPY = {
  load: {
    verb: 'Loaded',
    success: 'Demo data loaded',
    partial: 'Demo data partially loaded',
    failure: 'Could not load demo data'
  },
  reset: {
    verb: 'Reset',
    success: 'Demo data reset',
    partial: 'Demo data partially reset',
    failure: 'Could not reset demo data'
  }
} as const;

/**
 * The toast for a Load/Reset fan-out. "Partially" only when at least one service succeeded -
 * Bug #9113: every part failed and nothing was seeded, yet the toast said "partially loaded".
 */
export const describeDemoDataOutcome = (
  action: keyof typeof OUTCOME_COPY,
  result: PlaygroundDemoDataActionResult
): { title: string; description: string; color: 'success' | 'warning' | 'danger' } => {
  const copy = OUTCOME_COPY[action];
  const parts: string[] = [];
  if (result.succeeded.length > 0) {
    parts.push(`${copy.verb}: ${result.succeeded.join(', ')}`);
  }
  if (result.failed.length > 0) {
    parts.push(`Failed: ${result.failed.join(', ')}`);
  }
  const description = parts.join('. ');

  if (result.failed.length === 0) {
    return { title: copy.success, description, color: 'success' };
  }
  if (result.succeeded.length === 0) {
    return { title: copy.failure, description, color: 'danger' };
  }
  return { title: copy.partial, description, color: 'warning' };
};

/**
 * Seeds the demo data. Idempotent, so it is safe to run on every Playground turn-on.
 *
 * #9141: there is no "Load Demo Data" button any more. The design prototype shows demo data
 * the moment Playground is turned ON, so PlaygroundModeMenuToggle runs this after a successful
 * turn-on with `silentOnSuccess` (the design shows no toast for it). Failures still toast.
 */
export const useLoadPlaygroundDemoDataMutation = ({
  silentOnSuccess = false
}: { silentOnSuccess?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const seedOnboarding = $onboardingWorkflowApi.useMutation(
    'post',
    '/api/onboarding-workflows/playground/demo-data/seed-onboarded-supplier'
  );
  const seedBbbee = $bbbeeWebApi.useMutation(
    'post',
    '/api/bbbee/playground/demo-data/seed-rating'
  );
  const seedCoi = $coiApi.useMutation(
    'post',
    '/api/coi/playground/demo-data/seed-screening'
  );

  return useMutation({
    mutationFn: () =>
      runAcrossServices([
        { service: 'Onboarding', run: () => seedOnboarding.mutateAsync({}) },
        { service: 'BBBEE', run: () => seedBbbee.mutateAsync({}) },
        { service: 'COI', run: () => seedCoi.mutateAsync({}) }
      ]),
    onSuccess: (result) => {
      // Review PR #2646, fp:demo-data-no-query-invalidation: without this, every
      // supplier-list/dashboard/BBBEE-rating query the user is already looking at keeps serving
      // pre-seed cached data until a hard refresh. A broad invalidate is fine for a QA-only action.
      void queryClient.invalidateQueries();
      if (silentOnSuccess && result.failed.length === 0) {
        return;
      }
      toast(describeDemoDataOutcome('load', result));
    },
    onError: (error) => {
      console.error('QA Playground "Load Demo Data" failed unexpectedly:', error);
      toast({ title: 'Could not load demo data', color: 'danger' });
    }
  });
};

/** "Reset Demo Data": wipes and re-seeds each service's own demo records. */
export const useResetPlaygroundDemoDataMutation = () => {
  const queryClient = useQueryClient();
  const resetOnboarding = $onboardingWorkflowApi.useMutation(
    'post',
    '/api/onboarding-workflows/playground/demo-data/reset'
  );
  const resetBbbee = $bbbeeWebApi.useMutation(
    'post',
    '/api/bbbee/playground/demo-data/reset'
  );
  const resetCoi = $coiApi.useMutation('post', '/api/coi/playground/demo-data/reset');

  return useMutation({
    mutationFn: () =>
      runAcrossServices([
        { service: 'Onboarding', run: () => resetOnboarding.mutateAsync({}) },
        { service: 'BBBEE', run: () => resetBbbee.mutateAsync({}) },
        { service: 'COI', run: () => resetCoi.mutateAsync({}) }
      ]),
    onSuccess: (result) => {
      // Review PR #2646, fp:demo-data-no-query-invalidation: without this, already-rendered rows
      // keep pointing at ids that are now soft-deleted/replaced until a hard refresh.
      void queryClient.invalidateQueries();
      toast(describeDemoDataOutcome('reset', result));
    },
    onError: (error) => {
      console.error('QA Playground "Reset Demo Data" failed unexpectedly:', error);
      toast({ title: 'Could not reset demo data', color: 'danger' });
    }
  });
};
